import { supabase } from './supabase';

/**
 * 웹 푸시 클라이언트 라이브러리
 *
 * 플랫폼 감지 → 서비스워커 등록 → pushManager 구독 → 서버 저장(save_push_subscription RPC)
 * → 퍼널 이벤트 기록(push_funnel_events)까지 담당한다.
 *
 * 플랫폼 규칙:
 * - 안드로이드/데스크톱: 브라우저에서 바로 구독 가능
 * - iOS: 홈 화면에 설치된 웹앱(standalone)에서만 푸시 API가 존재한다 (iOS 16.4+).
 *   Safari 브라우저 상태에서는 설치 유도가 유일한 경로다.
 *
 * VAPID 공개키는 비밀이 아니므로 상수로 관리한다 (개인키는 Supabase 엣지 시크릿).
 */

export const VAPID_PUBLIC_KEY =
    'BKASq6I5hoarS-tHOvkMJdM7Sc3Cr0Wb4-mrIbrt30aCJmsHCvxMMrqKo_hHH9-ha8v1WO5AidCf8qkKdg4C8n4';

export type PushPlatform = 'ios-pwa' | 'ios-browser' | 'android' | 'desktop' | 'other';
export type PushSupport =
    | 'ready' | 'ios-needs-install' | 'ios-needs-safari'
    | 'android-needs-external' | 'unsupported';
export type PushFunnelEvent =
    | 'shown' | 'accepted' | 'granted' | 'denied' | 'dismissed' | 'later'
    | 'install_guide_shown' | 'pwa_installed' | 'subscribed' | 'unsubscribed'
    | 'unlocked' // 분석 게이트 통과=결과 해제 (source='analysis_gate' 실험, 2026-07-27)
    | 'resumed'; // 서버 복원(?resume=1)으로 저장된 분석을 다시 열람 (2026-08-15) — 면제 경유
                 // 열람은 unlocked가 안 찍히므로 이 이벤트가 없으면 열람률이 과소집계된다

const SNOOZE_KEY = 'sdl_push_snooze_until';
const INSTALL_LOGGED_KEY = 'sdl_pwa_install_logged';
// 유저가 설정에서 직접 끈 상태 — 이 플래그가 있으면 자동 재동기화가 절대 재구독시키지 않는다
const USER_DISABLED_KEY = 'sdl_push_user_disabled';
const SNOOZE_DAYS = 7;

// 재동기화는 세션(탭)당 1회면 충분 — 라우트 이동마다 DB 쓰기가 나가는 것을 방지
let syncedThisSession = false;

// 프리퍼미션 모달 인스턴스가 여러 곳(자동 게이트·벨·배너)에 있어도 동시에 겹치지 않게 하는 전역 플래그
let pushModalVisible = false;
export const setPushModalVisible = (visible: boolean): void => { pushModalVisible = visible; };
export const isPushModalVisible = (): boolean => pushModalVisible;

// pwa_installed 이중 기록 방지 (라우트 전환으로 Gate effect가 겹쳐 실행될 때의 TOCTOU)
let installLogInFlight = false;

// ---------------------------------------------------------------------------
// 플랫폼 감지
// ---------------------------------------------------------------------------

export const isIos = (): boolean =>
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    // iPadOS 13+는 데스크톱 UA로 위장하므로 터치 지원 Mac으로 판별
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

export const isStandalone = (): boolean =>
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true;

/** 스레드 앱 안 브라우저 — 아이폰 UA에는 'Instagram'도 'Safari'도 없고 메타 내부 이름 'Barcelona'만 있다 (대표 아이폰 실측 2026-10-30) */
export const isThreadsInAppBrowser = (): boolean => /Barcelona/i.test(navigator.userAgent);

/** 인앱 브라우저(카카오톡·인스타·스레드·페북 등) — 공유시트가 없거나 달라 '홈 화면에 추가' 불가 */
export const isInAppBrowser = (): boolean =>
    /KAKAOTALK|Instagram|Barcelona|FBAN|FBAV|FB_IAB|Line\/|NAVER|DaumApps|Snapchat|Twitter|TikTok|; wv\)/i
        .test(navigator.userAgent);

/** iOS에서 Safari 본체인지 (Chrome iOS·인앱 브라우저는 홈 화면 설치로 푸시 불가) */
export const isIosSafari = (): boolean =>
    isIos() &&
    !/CriOS|FxiOS|EdgiOS|OPiOS|OPT\/|Whale/i.test(navigator.userAgent) &&
    !isInAppBrowser();

/**
 * 인앱/타 브라우저에서 기본 브라우저(Safari)로 핸드오프하는 URL — 환경별 최적 경로.
 * - 카카오톡: kakaotalk://web/openExternal (알림톡 유입의 정석 탈출로)
 * - LINE: ?openExternalBrowser=1 파라미터
 * - 그 외 iOS(Chrome 등): x-safari- 스킴으로 Safari 열기 시도 (미지원 기기는 무동작 → 복사 폴백)
 */
export function getExternalBrowserUrl(): string | null {
    const href = window.location.href;
    const ua = navigator.userAgent;
    if (/KAKAOTALK/i.test(ua)) {
        return `kakaotalk://web/openExternal?url=${encodeURIComponent(href)}`;
    }
    if (/Line\//i.test(ua)) {
        return href + (href.includes('?') ? '&' : '?') + 'openExternalBrowser=1';
    }
    if (isIos()) {
        return `x-safari-${href}`;
    }
    if (/Android/i.test(ua)) {
        // 안드로이드 인앱 웹뷰 → 기본 브라우저로 핸드오프 (intent 스킴)
        const { host, pathname, search } = window.location;
        return `intent://${host}${pathname}${search}#Intent;scheme=https;end`;
    }
    return null;
}

/** 현재 주소(또는 넘겨받은 주소)를 클립보드에 복사 (웹뷰 호환 폴백 포함) */
export async function copyCurrentUrl(href: string = window.location.href): Promise<boolean> {
    try {
        await navigator.clipboard.writeText(href);
        return true;
    } catch {
        try {
            const textarea = document.createElement('textarea');
            textarea.value = href;
            textarea.style.position = 'fixed';
            textarea.style.opacity = '0';
            document.body.appendChild(textarea);
            textarea.select();
            const ok = document.execCommand('copy');
            document.body.removeChild(textarea);
            return ok;
        } catch {
            return false;
        }
    }
}

export function getPushPlatform(): PushPlatform {
    if (isIos()) return isStandalone() ? 'ios-pwa' : 'ios-browser';
    if (/Android/i.test(navigator.userAgent)) return 'android';
    if (/Mobi/i.test(navigator.userAgent)) return 'other';
    return 'desktop';
}

/**
 * 알림 유도(배너·벨·자동 모달)를 노출할 기기인지 — 모바일 전용 (다니엘 결정 2026-07-14).
 * 데스크톱은 유도하지 않되, 기존 구독 유지·설정 토글·?push_demo=1 테스트는 계속 동작한다.
 */
export const isPushPromotionTarget = (): boolean => getPushPlatform() !== 'desktop';

export function getPushSupport(): PushSupport {
    // iOS 비설치 상태를 API 존재 여부보다 먼저 판정 — 일부 iOS 버전은 브라우저 탭에서도
    // Notification/PushManager 전역을 노출하지만 실제 구독은 설치된 웹앱에서만 가능하다
    if (isIos() && !isStandalone()) {
        return isIosSafari() ? 'ios-needs-install' : 'ios-needs-safari';
    }
    // 안드로이드 인앱 웹뷰(카카오톡 등)는 API가 존재하는 척하면서 권한 창이 뜨지 않거나
    // requestPermission이 영원히 pending되는 사례가 있어 외부 브라우저로 유도한다
    if (/Android/i.test(navigator.userAgent) && isInAppBrowser() && !isStandalone()) {
        return 'android-needs-external';
    }
    const hasApis =
        'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
    if (hasApis) return 'ready';
    return 'unsupported';
}

// ---------------------------------------------------------------------------
// 퍼널 이벤트 기록 (로그인 유저만 — 유저 단위 추적이 목적)
// ---------------------------------------------------------------------------

export async function logPushFunnelEvent(
    event: PushFunnelEvent,
    opts: { source?: string } = {}
): Promise<void> {
    try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) return;
        await supabase.from('push_funnel_events').insert({
            user_id: session.user.id,
            event,
            platform: getPushPlatform(),
            source: (opts.source ?? 'organic').slice(0, 20),
            standalone: isStandalone(),
            user_agent: navigator.userAgent.slice(0, 500),
        });
    } catch (error) {
        console.warn('푸시 퍼널 이벤트 기록 실패:', error);
    }
}

// ---------------------------------------------------------------------------
// 서비스워커 · 구독
// ---------------------------------------------------------------------------

function urlBase64ToUint8Array(base64String: string) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    // applicationServerKey는 SharedArrayBuffer 불가 — 명시적 ArrayBuffer 기반으로 생성
    const outputArray = new Uint8Array(new ArrayBuffer(rawData.length));
    for (let i = 0; i < rawData.length; i += 1) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

async function getServiceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
    const existing = await navigator.serviceWorker.getRegistration('/');
    if (!existing) {
        await navigator.serviceWorker.register('/sw.js');
    }
    return navigator.serviceWorker.ready;
}

interface EnsureResult {
    ok: boolean;
    isNew?: boolean;
    reason?: string;
}

/**
 * 권한이 granted인 상태에서 구독을 만들고(없으면) 서버에 저장한다.
 * isNew: 이번에 브라우저 구독을 새로 만들었는지 (퍼널 이벤트 기록 판단용)
 */
export async function ensurePushSubscription(
    opts: { source?: string; logEvent?: boolean } = {}
): Promise<EnsureResult> {
    try {
        if (getPushSupport() !== 'ready') return { ok: false, reason: 'unsupported' };
        if (Notification.permission !== 'granted') return { ok: false, reason: 'not-granted' };

        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) return { ok: false, reason: 'not-authenticated' };

        const registration = await getServiceWorkerRegistration();
        let subscription = await registration.pushManager.getSubscription();
        const isNew = !subscription;
        if (!subscription) {
            subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
            });
        }

        const json = subscription.toJSON();
        if (!json.keys?.p256dh || !json.keys?.auth) {
            return { ok: false, reason: 'invalid-subscription' };
        }

        const platform = getPushPlatform();
        const { error } = await supabase.rpc('save_push_subscription', {
            p_endpoint: subscription.endpoint,
            p_p256dh: json.keys.p256dh,
            p_auth: json.keys.auth,
            p_platform: platform === 'ios-browser' ? 'other' : platform,
            p_user_agent: navigator.userAgent.slice(0, 500),
        });
        if (error) throw error;

        if (opts.logEvent !== false) {
            // 유저가 직접(모달/설정/벨/배너) 켠 경우 — '직접 끔' 플래그 해제 (로컬 + 서버)
            try { localStorage.removeItem(USER_DISABLED_KEY); } catch { /* 무시 */ }
            supabase.rpc('set_push_opt_out', { p_disabled: false }).then(
                ({ error: optError }) => { if (optError) console.warn('push_opt_outs 해제 실패:', optError); },
            );
            if (isNew) {
                await logPushFunnelEvent('subscribed', { source: opts.source });
            }
        }
        return { ok: true, isNew };
    } catch (error) {
        console.error('푸시 구독 실패:', error);
        return { ok: false, reason: error instanceof Error ? error.message : String(error) };
    }
}

/**
 * 권한은 이미 granted인데 서버 구독이 없거나 끊긴 경우 조용히 재동기화.
 * (브라우저 데이터 삭제, endpoint 교체, 계정 전환 등을 흡수한다)
 *
 * 절대 규칙 2가지:
 * - 유저가 직접 끈 상태면 재구독하지 않는다 — 로컬 플래그 + 서버(push_opt_outs) 이중 확인.
 *   iOS는 7일 미방문 시 로컬 저장소를 지울 수 있어 서버 기록이 원본이다.
 * - 세션당 1회만 실행 — 라우트 이동마다 DB 쓰기가 나가지 않게
 */
export async function syncExistingSubscription(): Promise<void> {
    if (syncedThisSession) return;
    try {
        if (localStorage.getItem(USER_DISABLED_KEY)) return;
    } catch { /* 프라이빗 모드 등 — 안전한 쪽(동기화 진행)으로 */ }
    if (getPushSupport() !== 'ready' || Notification.permission !== 'granted') return;

    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user) return;
    syncedThisSession = true;

    // 서버측 '직접 끔' 기록 확인 (RLS: 본인 행만 보임)
    try {
        const { data } = await supabase.from('push_opt_outs').select('user_id').limit(1);
        if (data && data.length > 0) {
            try { localStorage.setItem(USER_DISABLED_KEY, '1'); } catch { /* 무시 */ }
            return;
        }
    } catch { /* 조회 실패 시 안전한 쪽(재구독 보류)으로 */ return; }

    await ensurePushSubscription({ logEvent: false });
}

/** 설정 화면의 '끄기' — 브라우저 구독 해지 + 서버 revoke + 자동 재구독 차단 플래그 */
export async function unsubscribePush(): Promise<boolean> {
    try {
        const registration = await navigator.serviceWorker.getRegistration('/');
        const subscription = await registration?.pushManager.getSubscription();
        if (subscription) {
            await supabase.rpc('revoke_push_subscription', {
                p_endpoint: subscription.endpoint,
                p_reason: 'user_unsubscribed',
            });
            await subscription.unsubscribe();
        }
        // 유저의 명시적 의사 — 이후 자동 재동기화가 되살리지 못하게 기록 (로컬 + 서버)
        try { localStorage.setItem(USER_DISABLED_KEY, '1'); } catch { /* 무시 */ }
        const { error: optError } = await supabase.rpc('set_push_opt_out', { p_disabled: true });
        if (optError) console.warn('push_opt_outs 기록 실패:', optError);
        await logPushFunnelEvent('unsubscribed', { source: 'settings' });
        return true;
    } catch (error) {
        console.error('푸시 구독 해지 실패:', error);
        return false;
    }
}

export type PushState = 'subscribed' | 'granted-only' | 'default' | 'denied' | 'needs-install' | 'unsupported';

/** 현재 기기의 푸시 상태 (설정 화면 표시용) */
export async function getPushState(): Promise<PushState> {
    const support = getPushSupport();
    if (support === 'ios-needs-install' || support === 'ios-needs-safari' || support === 'android-needs-external') {
        return 'needs-install';
    }
    if (support !== 'ready') return 'unsupported';
    if (Notification.permission === 'denied') return 'denied';
    if (Notification.permission === 'default') return 'default';
    const registration = await navigator.serviceWorker.getRegistration('/');
    const subscription = await registration?.pushManager.getSubscription();
    return subscription ? 'subscribed' : 'granted-only';
}

// ---------------------------------------------------------------------------
// 무료 크레딧 리워드 여정 표식 (2026-07-17 캠페인)
// 카드 CTA 클릭 시 켜지고 지급 완료 시 꺼진다. iOS 설치 왕복 후 자동 지급의 근거이자,
// 게이트가 재유도 모달을 열 때 출처를 'credit_reward'로 이어받는 근거 — 캠페인 경유
// 구독이 'standalone'으로 새서 측정이 과소집계되는 것을 막는다.
// ---------------------------------------------------------------------------

const REWARD_PENDING_KEY = 'sdl_push_reward_pending';

export const hasPushRewardPending = (): boolean => {
    try { return localStorage.getItem(REWARD_PENDING_KEY) === '1'; } catch { return false; }
};

export const setPushRewardPending = (on: boolean): void => {
    try {
        if (on) localStorage.setItem(REWARD_PENDING_KEY, '1');
        else localStorage.removeItem(REWARD_PENDING_KEY);
    } catch { /* 프라이빗 모드 무시 */ }
};

// ---------------------------------------------------------------------------
// 노출 쿨다운 · 설치 감지
// ---------------------------------------------------------------------------

export const setPushSnooze = (days: number = SNOOZE_DAYS): void => {
    try {
        localStorage.setItem(SNOOZE_KEY, String(Date.now() + days * 24 * 60 * 60 * 1000));
    } catch { /* 프라이빗 모드 등에서 실패해도 무시 */ }
};

export const isPushSnoozed = (): boolean => {
    try {
        return Number(localStorage.getItem(SNOOZE_KEY) ?? 0) > Date.now();
    } catch {
        return false;
    }
};

/**
 * 홈 화면 웹앱(standalone)으로의 첫 실행을 감지해 pwa_installed 이벤트를 1회 기록.
 * iOS는 설치 완료 이벤트를 브라우저가 주지 않으므로 이 방식이 유일한 설치 판정이다.
 * 반환값: 이번 호출에서 처음으로 기록했는지 (직후 허용 유도의 골든 스팟 판단용)
 */
export async function logPwaInstallOnce(): Promise<boolean> {
    if (installLogInFlight) return false;
    installLogInFlight = true;
    try {
        if (!isStandalone()) return false;
        if (localStorage.getItem(INSTALL_LOGGED_KEY)) return false;
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user) return false;
        localStorage.setItem(INSTALL_LOGGED_KEY, '1');
        await logPushFunnelEvent('pwa_installed', { source: 'standalone' });
        return true;
    } catch {
        return false;
    } finally {
        installLogInFlight = false;
    }
}

/** 허용 직후 체험용 테스트 알림 (iOS PWA는 Notification 생성자가 없어 SW 경유 필수) */
export async function showTestNotification(): Promise<void> {
    const title = '소셜데이터랩';
    // 문구 원칙(2026-08-11): 지어낸 방문·조회수 금지 — 개통 확인임을 정직하게 알린다.
    // data.url: 탭하면 진행하던 분석으로 착지 (sw.js notificationclick이 data.url로 이동)
    const options: NotificationOptions = {
        body: '알림이 켜졌어요! 프로필 방문이 감지되면 이렇게 도착해요.',
        icon: '/icon-192x192.png',
        badge: '/icon-192x192.png',
        // local: 서버 푸시가 아님 — sw.js가 '푸시 누름' 표식을 붙이지 않는다 (2026-10-22)
        data: { url: '/visitor-analysis?resume=1', local: true },
    };
    try {
        const registration = await getServiceWorkerRegistration();
        await registration.showNotification(title, options);
    } catch {
        try {
            new Notification(title, options);
        } catch (error) {
            console.warn('테스트 알림 표시 실패:', error);
        }
    }
}
